import { ref, onMounted, onUnmounted } from 'vue';

/**
 * Creates a throttled version of a function
 * @param fn The function to throttle
 * @param delay Delay in milliseconds
 * @returns Throttled function
 */
export function throttle<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let lastCall = 0;
  return function (...args: Parameters<T>) {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn(...args);
    }
  };
}

/**
 * Creates a debounced version of a function
 * @param fn The function to debounce
 * @param delay Delay in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return function (...args: Parameters<T>) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn(...args);
    }, delay);
  };
}

/**
 * Composable for throttled window resize events
 * @param callback Function to call on resize
 * @param delay Throttle delay in milliseconds (default: 150ms)
 * @returns Object with cleanup method
 */
export function useThrottledResize(callback: () => void, delay = 150) {
  const throttledCallback = throttle(callback, delay);

  onMounted(() => {
    window.addEventListener('resize', throttledCallback);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', throttledCallback);
  });

  return {
    cleanup: () => window.removeEventListener('resize', throttledCallback),
  };
}

/**
 * Composable for responsive breakpoint checking with throttling
 * @returns Object with reactive isMobile and windowWidth
 */
export function useResponsive() {
  const isMobile = ref(false);
  const windowWidth = ref(window.innerWidth);

  const updateDimensions = () => {
    windowWidth.value = window.innerWidth;
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isNarrow = window.innerWidth < 768;
    isMobile.value = hasTouch && isNarrow;
  };

  useThrottledResize(updateDimensions, 150);

  // Initial check
  updateDimensions();

  return {
    isMobile,
    windowWidth,
  };
}
